import React from 'react';

import { usePrefs } from 'wsm-common-data';
import { isInTransit } from 'wsm-highlighted-attribute';

import { getAttributeLabel } from '../../../utilities/getAttributeLabel';
import { formatCityStateDistance } from '../../../utilities/formatCityStateDistance';

import Delivery from './Delivery';
import LocationDistance from './LocationDistance';
import vehiclePropType from '../../../propTypes/vehiclePropType';
import { VEHICLE_STATUS_CONFIG } from '../../../global-constants';
import LocationStatus from './LocationStatus';

const LocationContainer = ({ vehicle }) => {
	const { deliveryEnabled, showVehicleStatus } = usePrefs();
	const { trackingAttributes = [], status, attributes = [], uuid } = vehicle;

	const geodistAttr = trackingAttributes.find(
		(attr) => attr.name === 'geodist'
	)?.value;
	const distance = geodistAttr ? parseInt(geodistAttr, 10) : null;
	const showDelivery =
		deliveryEnabled === 'true' && !isInTransit(status) && distance !== null;

	// Try cityStateDistance first (higher priority)
	const cityStateDistanceLabel = formatCityStateDistance(
		getAttributeLabel(attributes, 'cityStateDistance')
	);

	// Fallback to locationDistance if cityStateDistance is not available.
	// Don't show locationDistance if vehicle is in transit.
	const locationDistanceRaw = getAttributeLabel(
		attributes,
		'locationDistance'
	);
	const locationDistanceLabel =
		!cityStateDistanceLabel && !isInTransit(status)
			? locationDistanceRaw
			: null;

	// Use cityStateDistance or fallback to locationDistance
	const finalLocationLabel = cityStateDistanceLabel || locationDistanceLabel;

	// Location section is enabled when location/distance IDC config is active
	const locationEnabled = Boolean(locationDistanceRaw);

	const statusConfig = VEHICLE_STATUS_CONFIG[status] || null;

	// Show vehicle status (In Transit / In Production) in place of location when applicable
	const showStatus =
		showVehicleStatus === 'true' &&
		locationEnabled &&
		Boolean(statusConfig);

	if (!showDelivery && !finalLocationLabel && !showStatus) return null;

	// Extract dealer name from locationDistance raw value (formatted as 'dealerName ::: geoDist')
	const dealerName = locationDistanceRaw
		? locationDistanceRaw.split(':::')[0].trim()
		: '';

	const renderLocationDistance = () => {
		if (!finalLocationLabel) return null;
		return (
			<LocationDistance
				label={finalLocationLabel}
				accountId={vehicle.accountId}
			/>
		);
	};

	return (
		<div className="mb-3">
			{showDelivery && <Delivery distance={distance} />}
			{showStatus ? (
				<LocationStatus
					dealerName={dealerName}
					status={status}
					vehicleUuid={uuid}
				/>
			) : (
				renderLocationDistance()
			)}
		</div>
	);
};

LocationContainer.propTypes = {
	vehicle: vehiclePropType.isRequired
};

export default LocationContainer;
